import Button from "@/components/Button"
import Dialog from "@/components/Dialog"
import InputErrorMessage from "@/components/InputErrorMessage"
import { SelectInput } from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import Spinner from "@/components/Spinner"
import { useDeviceOrderKitsQuery } from "@/data/queries"
import { ClinicalProtocolType } from "@/model/ClinicalProtocol"
import { DeviceKit, DeviceKitType } from "@/model/DeviceOrders"
import { deviceVendorNameFor } from "@/model/Devices"
import { UsPostalAddress } from "@/model/Patient"
import cn from "@/utils/cn"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"

type OrderDeviceFormData = {
  deviceKitId: string
}

type OrderDeviceDialogProps = {
  isOpen: boolean
  onClose: () => void
  patientName: string
  shippingAddress?: UsPostalAddress
  attCompatibility?: boolean
  diagnosis: string
  prescriptionTypes: ClinicalProtocolType[]
  onOrder: (kit: DeviceKit) => Promise<void>
  isOrdering: boolean
}

const kitTypeFor = (prescriptionType: ClinicalProtocolType): DeviceKitType =>
  prescriptionType === "hypertension"
    ? "blood-pressure-monitor"
    : "blood-glucose-monitor"

const formSchema = z.object({
  deviceKitId: z.string().min(1, { message: "Device kit is required" }),
})

const OrderDeviceDialog = ({
  isOpen,
  onClose,
  patientName,
  shippingAddress,
  diagnosis,
  prescriptionTypes,
  onOrder,
  isOrdering,
  attCompatibility,
}: OrderDeviceDialogProps) => {
  const { register, handleSubmit, formState, reset } =
    useForm<OrderDeviceFormData>({
      mode: "onChange",
      defaultValues: { deviceKitId: "" },
      resolver: zodResolver(formSchema),
    })
  const { errors } = formState

  const {
    data: kits = [],
    isLoading: isLoadingKits,
    isError: isKitsError,
  } = useDeviceOrderKitsQuery(isOpen)

  useEffect(() => {
    if (!isOpen) {
      reset({ deviceKitId: "" })
    }
  }, [isOpen, reset])

  const allowedKitTypes = prescriptionTypes.map(kitTypeFor)

  const filteredKits = kits.filter(({ type }) => allowedKitTypes.includes(type))

  const onSubmit = (formData: OrderDeviceFormData) => {
    const kit = filteredKits.find(({ id }) => id === formData.deviceKitId)

    if (!kit) return
    onOrder(kit)
  }

  // TODO: Figure out how we want to format the address
  const formatAddress = (address?: UsPostalAddress): string => {
    const lines: string[] = [
      address?.address1 || "",
      address?.address2 || "",
      address?.city || "",
      address?.state || "",
      address?.zipCode || "",
    ]

    return lines.length > 0 ? lines.join("\n") : "No shipping address available"
  }

  const selectOptions = [
    {
      value: "",
      title: isLoadingKits ? "Loading..." : "Select a device kit",
      disabled: true,
      hidden: false,
    },
    ...filteredKits.map((kit) => ({
      value: kit.id,
      title: `${deviceVendorNameFor(kit.vendor)} — ${kit.name}`,
    })),
  ]

  return (
    <Dialog
      className="max-w-lg"
      title="Order Device"
      isOpen={isOpen}
      onClose={onClose}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
        autoComplete="off"
      >
        <div className="flex flex-col">
          <div className="flex flex-row justify-between border-l-2 border-primary bg-gray-100 p-2">
            <InputLabel className="font-semibold">Patient Name</InputLabel>
            <p className="text-sm leading-6 text-primary/90">{patientName}</p>
          </div>

          <div className="flex flex-row justify-between border-l-2 border-primary p-2">
            <InputLabel className="font-semibold">Shipping Address</InputLabel>
            <p className="whitespace-pre-line text-right text-sm leading-6 text-primary/90">
              {formatAddress(shippingAddress)}
            </p>
          </div>

          <div className="flex flex-row justify-between border-l-2 border-primary bg-gray-100 p-2">
            <InputLabel className="font-semibold">
              AT&T Compatibility
            </InputLabel>
            <p className="flex items-center gap-1 text-sm leading-6 text-primary/90">
              {attCompatibility !== undefined ? (
                <>
                  <span
                    className={cn(
                      "material-icons align-middle text-[18px]",
                      attCompatibility ? "text-green-700" : "text-red-700"
                    )}
                  >
                    {attCompatibility ? "check" : "close"}
                  </span>
                  {attCompatibility ? "Compatible" : "Not Compatible"}
                </>
              ) : (
                <span className="text-gray-500">N/A</span>
              )}
            </p>
          </div>

          <div className="mb-5 flex flex-row justify-between border-l-2 border-primary p-2">
            <InputLabel className="font-semibold">Diagnosis</InputLabel>
            <p className="text-sm leading-6 text-primary/90">
              {diagnosis || "-"}
            </p>
          </div>

          <div className="mb-5 flex flex-col gap-1">
            <InputLabel htmlFor="deviceKitId">Device Kit</InputLabel>
            <SelectInput
              id="deviceKitId"
              options={selectOptions}
              disabled={isOrdering || isLoadingKits || isKitsError}
              {...register("deviceKitId")}
              className={
                errors?.deviceKitId?.message
                  ? "ring-red-600 focus:ring-red-600"
                  : ""
              }
            />
            <InputErrorMessage
              message={
                isKitsError
                  ? "Failed to load device kits. Try again."
                  : errors?.deviceKitId?.message
              }
              className="ml-1"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="neutral"
            onClick={onClose}
            disabled={isOrdering}
            className="px-6"
          >
            Cancel
          </Button>
          <Button
            className="flex items-center gap-3 px-6"
            type="submit"
            variant="primary"
            disabled={isOrdering || isLoadingKits || isKitsError}
          >
            {isOrdering && <Spinner className="h-4 w-4" />}
            Order
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

export default OrderDeviceDialog
